import { useEffect } from 'react';
import { Dispatch } from 'redux';

import {
  CandidateViewType,
  ErrorType,
  EventTimeSlot,
  ModelType,
} from '../../../../../interfaces/merchants/resources/books';
import * as Books from '../../../../reducers/merchants/resources/books';
import * as Inputs from '../../../../reducers/merchants/resources/books/inputs';
import * as Users from '../../../../reducers/users';

import * as aUser from '../../../users/api';
import * as a from './api';

import * as workflow from './workflow';
import { fetchMerchantDetail } from '../../../../../infrastructure/api';

export interface InitQuery {
  merchantId: string;
  resourceId: string;
  selectedDate?: string;
  selectedSlot?: string;
  selectedLesson?: string;
  selectedCourse?: string;
  selectedStaff?: string;
  loggedIn?: boolean;
  isEmailVerified: boolean;
}

type AllActions = Books.Actions | Inputs.Actions | Users.Actions;

const toDataSet = async ({
  merchantId,
  resourceId,
  modelType,
  candidateViewType,
  selectedDate,
  selectedSlot,
  selectedCourse,
  selectedStaff,
}: {
  merchantId: string;
  resourceId: string;
  modelType: string;
  candidateViewType: CandidateViewType;
  selectedDate?: string;
  selectedSlot?: string;
  selectedCourse?: string;
  selectedStaff?: string;
}): Promise<Books.DataSet> => {
  switch (modelType) {
    case ModelType.AvailabilityScheme:
      return {
        modelType,
        candidateViewType,
        selectedDate,
        courseData: await a.fetchCourse(merchantId, resourceId),
        customFieldsData: await a.fetchCustomFields(merchantId, resourceId),
        confirmData: await a.fetchConfirmation(merchantId, resourceId),
        selectedCourseData: selectedCourse
          ? await a.fetchSelectedCourse(merchantId, resourceId, selectedCourse)
          : undefined,
      };
    case ModelType.EventScheme:
      return {
        modelType,
        candidateViewType,
        selectedTimeSlotData: selectedSlot
          ? await a.fetchSelectedTimeSlot(merchantId, resourceId, selectedSlot)
          : undefined,
        eventData: await a.fetchEvent(merchantId, resourceId),
        customFieldsData: await a.fetchCustomFields(merchantId, resourceId),
        confirmData: await a.fetchConfirmation(merchantId, resourceId),
      };
    case ModelType.AssignScheme:
      return {
        modelType,
        candidateViewType,
        selectedDate,
        courseData: await a.fetchCourse(merchantId, resourceId),
        staffData: await a.fetchStaff(merchantId, resourceId),
        customFieldsData: await a.fetchCustomFields(merchantId, resourceId),
        confirmData: await a.fetchConfirmation(merchantId, resourceId),
        selectedCourseData: selectedCourse
          ? await a.fetchSelectedCourse(merchantId, resourceId, selectedCourse)
          : undefined,
        selectedStaffData: selectedStaff
          ? await a.fetchSelectedStaff(merchantId, resourceId, selectedStaff)
          : undefined,
      };
    case ModelType.SchoolScheme:
      return {
        modelType,
        candidateViewType,
        selectedTimeSlotData: selectedSlot
          ? await a.fetchSelectedTimeSlot(merchantId, resourceId, selectedSlot)
          : undefined,
        eventData: await a.fetchEvent(merchantId, resourceId),
        lessonData: await a.fetchLesson(merchantId, resourceId),
        customFieldsData: await a.fetchCustomFields(merchantId, resourceId),
        confirmData: await a.fetchConfirmation(merchantId, resourceId),
      };
    default:
      throw new Error(`require new implementation of "${modelType}"`);
  }
};

export const init =
  (q: InitQuery) => async (dispatch: Dispatch<AllActions>) => {
    const { merchantId, resourceId } = q;
    const usersMeData = await aUser.fetchUsersMe();
    const resourceData = await a.fetchResource(merchantId, resourceId);
    // FIXME: 本来initの引数として渡せるはず... (ページで既にfetchMerchantDetailされているため)
    const merchantdetailResponse = await fetchMerchantDetail({
      session: '',
      token: '',
      requestId: '',
      merchantPublicId: merchantId,
      skipHostCheck: true,
    });
    if (merchantdetailResponse.redirect) throw 'never';
    const merchantDetail = merchantdetailResponse.merchant;
    const dataSetAttr = {
      modelType: resourceData.model_type,
      merchantId,
      resourceId,
      // マツバツ、カレンダー、リストのどれか
      candidateViewType: resourceData.view_type,
      selectedDate: q.selectedDate,
      selectedSlot: q.selectedSlot,
      selectedLesson: q.selectedLesson,
      selectedCourse: q.selectedCourse,
      selectedStaff: q.selectedStaff,
    };
    const dataSet = await toDataSet(dataSetAttr);

    if (usersMeData) {
      dispatch({
        payload: {
          creditCard: usersMeData.credit_card
            ? {
                canonicalId: usersMeData.credit_card.canonical_id,
                expireMonth: usersMeData.credit_card.expire_month,
                expireYear: usersMeData.credit_card.expire_year,
                tailDigits: usersMeData.credit_card.tail_digits,
              }
            : undefined,
          displayName: usersMeData.display_name,
          email: usersMeData.email,
          firstname: usersMeData.firstname,
          id: usersMeData.id,
          isEmailVerified: usersMeData.is_email_verified,
          lastname: usersMeData.lastname,
          phoneNumber: usersMeData.phone_number,
          publicId: usersMeData.public_id,
        },
        type: Users.Types.Me,
      });
    }
    dispatch({
      payload: { merchantId: q.merchantId, resourceId: q.resourceId },
      type: Inputs.Types.Init,
    });
    if (merchantDetail) {
      dispatch({
        payload: {
          ...q,
          dataSet,
          resourceData,
          loggedIn: !!usersMeData,
          jcbEnabled: merchantDetail.jcb_enabled,
          isEmailVerified: q.isEmailVerified,
        },
        type: Books.Types.Init,
      });
    }
    // Books.Types.InitでなぜかtimeSlotが空でinitializeされるのでSetDateTimePickerをdispatchする。
    // 複雑すぎて謎なのでやめたい...
    const timeSlots = await a.fetchTimeSlots({
      merchantId,
      resourceId,
      start: null,
      end: null,
    });
    dispatch({
      payload: { timeSlots },
      type: Books.Types.SetDateTimePicker,
    });

    workflow.init();
  };

export const nextStep = (step: number) => (dispatch: Dispatch<Books.Actions>) =>
  dispatch({
    type: Books.Types.SetStep,
    payload: {
      step: workflow.nextStep(step, false),
    },
  });

export const finalStep =
  (step: number) => (dispatch: Dispatch<Books.Actions>) =>
    dispatch({
      type: Books.Types.SetStep,
      payload: {
        step: workflow.nextStep(step, true),
      },
    });

export const goTo = (step: number) => (dispatch: Dispatch<Books.Actions>) => {
  dispatch({ type: Books.Types.SetStep, payload: { step } });
};

export const clearCalendar = () => (dispatch: Dispatch<Books.Actions>) => {
  dispatch({
    payload: { events: { checkin_times: [], dates: {} } },
    type: Books.Types.SetCalendar,
  });
};

export const setCalendar =
  (q: a.CalendarQuery) => async (dispatch: Dispatch<Books.Actions>) => {
    const events = await a.fetchEvents(q);
    dispatch({
      payload: { events },
      type: Books.Types.SetCalendar,
    });
  };

export const clearDateTimePicker =
  () => (dispatch: Dispatch<Books.SetDateTimePickerAction>) => {
    dispatch({
      payload: {
        timeSlots: {
          meta: { staff: [], waiting_list_provided: false, waiting_listed: [] },
          data: [],
        },
      },
      type: Books.Types.SetDateTimePicker,
    });
  };

export const setDateTimePicker =
  (q: a.DateTimePickerQuery) =>
  async (
    dispatch: Dispatch<Books.SetDateTimePickerAction | Books.SetPostableAction>,
  ) => {
    dispatch({ payload: { postable: false }, type: Books.Types.SetPostable });
    const timeSlots = await a.fetchTimeSlots(q);
    dispatch({ payload: { postable: true }, type: Books.Types.SetPostable });
    dispatch({
      payload: { timeSlots },
      type: Books.Types.SetDateTimePicker,
    });
  };

export const setAvailableDates =
  (q: a.AvailableDateTimeQuery) =>
  async (dispatch: Dispatch<Books.SetAvailableDatesAction>) => {
    const availableDates = await a.fetchAvailableDates(q);
    dispatch({
      payload: { availableDates },
      type: Books.Types.SetAvailableDates,
    });
  };

export const setAvailableTimes =
  (q: a.AvailableDateTimeQuery) =>
  async (dispatch: Dispatch<Books.SetAvailableTimesAction>) => {
    const availableTimes = await a.fetchAvailableTimes(q);
    const selectedDate = [
      q.date.getFullYear(),
      `0${q.date.getMonth() + 1}`.slice(-2),
      `0${q.date.getDate()}`.slice(-2),
    ].join('-');
    dispatch({
      payload: { availableTimes, selectedDate },
      type: Books.Types.SetAvailableTimes,
    });
  };

export const setMeToBookInfo =
  (merchantId: string, resourceId: string) =>
  async (dispatch: Dispatch<AllActions>) => {
    dispatch({
      payload: {
        customFields: await a.fetchCustomFields(merchantId, resourceId),
      },
      type: Books.Types.SetCustomFields,
    });
    const usersMeData = await aUser.fetchUsersMe();
    if (usersMeData) {
      dispatch({
        payload: {
          creditCard: usersMeData.credit_card
            ? {
                canonicalId: usersMeData.credit_card.canonical_id,
                expireMonth: usersMeData.credit_card.expire_month,
                expireYear: usersMeData.credit_card.expire_year,
                tailDigits: usersMeData.credit_card.tail_digits,
              }
            : undefined,
          displayName: usersMeData.display_name,
          email: usersMeData.email,
          firstname: usersMeData.firstname,
          id: usersMeData.id,
          isEmailVerified: usersMeData.is_email_verified,
          lastname: usersMeData.lastname,
          phoneNumber: usersMeData.phone_number,
          publicId: usersMeData.public_id,
        },
        type: Users.Types.Me,
      });
    }
  };

export const addToWaitingList =
  (
    merchantPublicId: string,
    resourceId: string,
    timeSlot: EventTimeSlot,
    lastName?: string,
    firstName?: string,
    email?: string,
  ) =>
  async (
    dispatch: Dispatch<
      | Books.AddToWaitingListAction
      | Books.AddToWaitingListErrorsAction
      | Books.SetPostableAction
    >,
  ) => {
    dispatch({ payload: { postable: false }, type: Books.Types.SetPostable });
    const res = await a.addToWaitingList(
      merchantPublicId,
      resourceId,
      timeSlot.id,
      lastName,
      firstName,
      email,
    );
    dispatch({ payload: { postable: true }, type: Books.Types.SetPostable });
    if (!res.success && res.errors!.email!.indexOf(ErrorType.Taken) === -1) {
      dispatch({
        payload: { errors: res.errors! },
        type: Books.Types.AddToWaitingListErrors,
      });
      throw new Error('post api failed');
    }
    dispatch({
      payload: { timeSlot },
      type: Books.Types.AddToWaitingList,
    });
  };

// browserの戻るボタンなどでpopstateイベントが発生した時に、storeの状態を更新するフック
// next/routerのbeforePopStateはpathが同じ場合には実行されないため、windowに直接イベントリスナを設定
// https://github.com/vercel/next.js/blob/v12.0.1/packages/next/shared/lib/router/router.ts#L825-L833
export const useWindowPopstate: (params: { dispatch: Dispatch }) => void = ({
  dispatch,
}) => {
  useEffect(() => {
    const onWindowPopstateHandler = () => {
      const payload = workflow.setup(history.state && history.state.options);
      if (payload) {
        dispatch({ payload, type: Books.Types.SetStep });
      }
    };

    window.addEventListener('popstate', onWindowPopstateHandler);

    return () => {
      window.removeEventListener('popstate', onWindowPopstateHandler);
    };
  }, [dispatch]);
};
